import React, { useMemo } from 'react';
import {
  Datagrid,
  TextField,
  FunctionField,
  useTranslate,
  useRecordContext,
  ListController,
  ListContextProvider,
  Filter,
  TextInput,
  SelectInput,
  useListContext,
} from 'react-admin';
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
  Typography,
} from '@material-ui/core';
import { useLocation, useHistory } from 'react-router-dom';
import { InventorizationStatus, InventorizationIncorrectStatus } from '../../../entities/InventorizationStatus';
import { SelectEnumInput } from '../../../components/inputs/SelectEnumInput';
import { enumToPlain } from '../../../utils/enumUtils';
import { getStatusColor, getStatusLabel, getIncorrectStatusLabel } from '../utils/inventorizationHelpers';
import { PalletInfo } from '../services/configApi';

const StatusChipField: React.FC = () => {
  const record = useRecordContext();
  const theme = useTheme();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <Chip
      label={getStatusLabel(record.status, translate)}
      size="small"
      style={{
        backgroundColor: getStatusColor(record.status, theme),
        color: '#fff',
      }}
    />
  );
};

const IncorrectStatusField: React.FC = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  return <>{getIncorrectStatusLabel(record.incorrect_status, translate)}</>;
};

const PalletsField: React.FC = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record?.pallets?.length) {
    return <span>-</span>;
  }

  return (
    <Table size="small" style={{ margin: 0 }}>
      <TableHead>
        <TableRow>
          <TableCell style={{ padding: '2px 8px' }}>{translate('osfc.inventorization.columns.pallet_id')}</TableCell>
          <TableCell style={{ padding: '2px 8px' }}>{translate('osfc.inventorization.columns.pallet_sscc')}</TableCell>
          <TableCell style={{ padding: '2px 8px' }}>{translate('osfc.inventorization.columns.ceel_number')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {record.pallets.map((pallet: PalletInfo) => (
          <TableRow key={pallet.pallet_id}>
            <TableCell style={{ padding: '2px 8px' }}>{pallet.pallet_id}</TableCell>
            <TableCell style={{ padding: '2px 8px' }}>{pallet.pallet_sscc || '-'}</TableCell>
            <TableCell style={{ padding: '2px 8px' }}>{pallet.ceel_number || '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const InventorizationFilters: React.FC = (props) => {
  const translate = useTranslate();

  const hasPalletChoices = [
    { id: 'true', name: translate('osfc.inventorization.filter.pallet_exists.yes') },
    { id: 'false', name: translate('osfc.inventorization.filter.pallet_exists.no') },
  ];

  return (
    <Filter {...props}>
      <TextInput
        source="place_address"
        label={translate('osfc.inventorization.columns.place_address')}
        alwaysOn
        resettable
      />
      <SelectEnumInput
        source="status"
        label={translate('osfc.inventorization.columns.status')}
        enumObj={enumToPlain(InventorizationStatus)}
        enumName="inventorization_status"
        alwaysOn
        resettable
        allowEmpty
        emptyText={translate('osfc.inventorization.filter.all')}
      />
      <SelectEnumInput
        source="incorrect_status"
        label={translate('osfc.inventorization.columns.incorrect_status')}
        enumObj={enumToPlain(InventorizationIncorrectStatus)}
        enumName="inventorization_incorrect_status"
        alwaysOn
        resettable
        allowEmpty
        emptyText={translate('osfc.inventorization.filter.all')}
        style={{ minWidth: 200 }}
      />
      <SelectInput
        source="has_pallet"
        label={translate('osfc.inventorization.filter.pallet_exists.label')}
        choices={hasPalletChoices}
        alwaysOn
        resettable
        allowEmpty
        emptyText={translate('osfc.inventorization.filter.all')}
        style={{ minWidth: 180 }}
      />
      <TextInput
        source="pallet_sscc"
        label={translate('osfc.inventorization.columns.pallet_sscc')}
        alwaysOn
        resettable
      />
      <TextInput
        source="ceel_number"
        label={translate('osfc.inventorization.columns.ceel_number')}
        alwaysOn
        resettable
      />
      <TextInput
        source="note"
        label={translate('osfc.inventorization.columns.note')}
        alwaysOn
        resettable
      />
      <TextInput
        source="sscc"
        label={translate('osfc.inventorization.columns.sscc')}
        alwaysOn
        resettable
      />
    </Filter>
  );
};

const TotalCount: React.FC = () => {
  const translate = useTranslate();
  const { total } = useListContext();

  return (
    <div style={{ marginTop: 8 }}>
      <Typography variant="body2" color="textSecondary">
        {translate('ra.navigation.page_rows_per_page')}: {total ?? 0}
      </Typography>
    </div>
  );
};

const InventorizationDatagrid: React.FC = () => {
  const translate = useTranslate();

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Datagrid>
          <TextField source="place_address" label={translate('osfc.inventorization.columns.place_address')} />
          <FunctionField source="status" label={translate('osfc.inventorization.columns.status')} render={() => <StatusChipField />} />
          <FunctionField source="incorrect_status" label={translate('osfc.inventorization.columns.incorrect_status')} render={() => <IncorrectStatusField />} />
          <TextField source="note" label={translate('osfc.inventorization.columns.note')} emptyText="-" />
          <TextField source="sscc" label={translate('osfc.inventorization.columns.sscc')} emptyText="-" />
          <TextField source="quantity" label={translate('osfc.inventorization.columns.quantity')} emptyText="-" />
          <FunctionField label={translate('osfc.inventorization.columns.pallets_count')} render={() => <PalletsField />} sortable={false} />
        </Datagrid>
      </div>
      <TotalCount />
    </div>
  );
};

interface InventorizationStatusesListProps {
  terminalId: number;
}

export const InventorizationStatusesList: React.FC<InventorizationStatusesListProps> = ({
  terminalId,
}) => {
  const location = useLocation();
  const history = useHistory();

  const filter = useMemo(() => ({
    terminal_id: terminalId,
    no_range: true
  }), [terminalId]);

  return (
    <ListController
      resource="storage-inventorization-statuses"
      basePath="/storage-inventorization-statuses"
      location={location}
      history={history}
      syncWithLocation={false}
      filter={filter}
      filterDefaultValues={{ status: InventorizationStatus.incorrect }}
      sort={{ field: 'place_address', order: 'ASC' }}
    >
      {(controllerProps) => (
        <ListContextProvider value={controllerProps}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flexShrink: 0 }}>
              <InventorizationFilters />
            </div>
            <InventorizationDatagrid />
          </div>
        </ListContextProvider>
      )}
    </ListController>
  );
};
